import { useState, useCallback, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { EXPENSE_SORT_DEFAULTS, useExpenseSortPref } from '../../../../hooks/useExpenseSortPref';
import * as actions from '../../../../redux/actions';
import api from '../../../../utils/functions/api';
import { sortTodayExpenses } from '../../../../utils/functions/sortTodayExpenses';
import { TABS, TODAY_SUBTABS } from '../constants/tabs';
import { getTabQueryParams } from '../utils/query';

// แปลง params object (จาก getTabQueryParams) → URLSearchParams สำหรับยิงนับจำนวน (size:1)
function toCountQuery(mode, params) {
  const q = new URLSearchParams({ size: '1', page: '1' });
  const {
    statuses,
    [mode.queryParam]: queueStatuses,
    dateStart,
    dateEnd,
    readyDateStart,
    readyDateEnd,
    urgentQueue,
    urgentSuccessStart,
    urgentSuccessEnd,
  } = params;
  if (statuses) q.set('statuses', statuses);
  if (queueStatuses) q.set(mode.queryParam, queueStatuses);
  if (dateStart) q.set('dateStart', dateStart.toISOString());
  if (dateEnd) q.set('dateEnd', dateEnd.toISOString());
  if (readyDateStart) q.set('readyDateStart', readyDateStart.toISOString());
  if (readyDateEnd) q.set('readyDateEnd', readyDateEnd.toISOString());
  if (urgentQueue) q.set('urgentQueue', urgentQueue);
  if (urgentSuccessStart) q.set('urgentSuccessStart', urgentSuccessStart.toISOString());
  if (urgentSuccessEnd) q.set('urgentSuccessEnd', urgentSuccessEnd.toISOString());
  return q.toString();
}

async function fetchTotal(queryString) {
  try {
    const { data, status } = await api.get(
      `${process.env.REACT_APP_API_URL}/expenses?${queryString}`,
    );
    return status === 200 ? data?.total ?? 0 : 0;
  } catch (err) {
    return 0;
  }
}

// ดึงข้อมูลของแท็บที่กำลังเปิดอยู่ (rows) + ตัวเลขจำนวนรายการของทุกแท็บพร้อมกัน (tabCounts) — สองอย่างนี้
// ยิงคนละ request กัน: rows ผ่าน redux dispatch(actions.expensesAll) ปกติ (เก็บใน state.expenses.rows)
// ส่วน tabCounts ยิงผ่าน api ตรงๆ (size:1 ต่อแท็บ) ไม่ผ่าน redux เพราะ reducer ตัวเดียวกันนั้นใช้
// state.expenses.rows ก้อนเดียวกับตารางหลัก ถ้า dispatch จะไปทับ rows ของแท็บที่เปิดอยู่ทันที
// mode (constants/modes.js) กำหนดว่ากรองด้วย paymentApprovalStatuses หรือ reviewStatuses
export function useQueueTabs(mode) {
  const dispatch = useDispatch();
  const fetchedRows = useSelector((state) => state.expenses?.rows);
  const isLoading = useSelector((state) => state.expenses?.isLoading);
  const [tab, setTab] = useState('today');
  // sub-tab ของแท็บ "วันนี้" (อนุมัติวันนี้/ด่วน/จ่ายเงินวันนี้) — ไม่มีผลกับแท็บอื่น
  const [subTab, setSubTab] = useState('approved');

  // เรียงตามค่า "เรียงตาม" ของแท็บคู่กันใน /finance/expenses (วันนี้/ค้าง/อนาคต) — Finance เปลี่ยนแล้วที่นี่
  // เปลี่ยนตาม (useExpenseSortPref.js) แท็บ "รอก่อน"/"ประวัติ" ไม่มีแท็บคู่ฝั่ง Finance คงวันที่ขอรับเงินล่าสุดก่อน
  const hasFinancePair = Boolean(EXPENSE_SORT_DEFAULTS[tab]);
  const financeSort = useExpenseSortPref(hasFinancePair ? tab : 'today');
  const sortBy = hasFinancePair ? financeSort.sortBy : 'date';
  const sortDir = hasFinancePair ? financeSort.sortDir : 'desc';

  // แท็บวันนี้ของ Finance เรียงซ้ำฝั่งหน้าเว็บทับลำดับจาก backend อีกชั้น (ExpensesTabToday.js) — ต้องทำ
  // เหมือนกันถึงจะได้ลำดับตรงกันเป๊ะ แท็บอื่นของ Finance ใช้ลำดับจาก backend ตรงๆ
  const rows = useMemo(() => {
    const list = fetchedRows || [];
    return tab === 'today' ? sortTodayExpenses(list, sortBy, sortDir) : list;
  }, [fetchedRows, tab, sortBy, sortDir]);

  const fetchRows = useCallback(() => {
    const {
      statuses,
      [mode.queryParam]: queueStatuses,
      dateStart,
      dateEnd,
      readyDateStart,
      readyDateEnd,
      urgentQueue,
      urgentSuccessStart,
      urgentSuccessEnd,
    } = getTabQueryParams(mode, tab, subTab);
    dispatch(
      actions.expensesAll({
        statuses,
        [mode.queryParam]: queueStatuses,
        dateStart,
        dateEnd,
        readyDateStart,
        readyDateEnd,
        urgentQueue,
        urgentSuccessStart,
        urgentSuccessEnd,
        sortBy,
        sortDir,
      }),
    );
  }, [dispatch, mode, tab, subTab, sortBy, sortDir]);

  useEffect(() => {
    fetchRows();
  }, [fetchRows]);

  const [tabCounts, setTabCounts] = useState({});
  const fetchTabCounts = useCallback(async () => {
    const entries = await Promise.all(
      TABS.map(async (t) => [
        t.key,
        await fetchTotal(toCountQuery(mode, getTabQueryParams(mode, t.key))),
      ]),
    );
    setTabCounts(Object.fromEntries(entries));
  }, [mode]);

  useEffect(() => {
    fetchTabCounts();
  }, [fetchTabCounts]);

  // จำนวนรายการต่อ sub-tab ของแท็บ "วันนี้"
  const [subTabCounts, setSubTabCounts] = useState({});
  const fetchSubTabCounts = useCallback(async () => {
    const entries = await Promise.all(
      TODAY_SUBTABS.map(async (s) => [
        s.key,
        await fetchTotal(toCountQuery(mode, getTabQueryParams(mode, 'today', s.key))),
      ]),
    );
    setSubTabCounts(Object.fromEntries(entries));
  }, [mode]);

  useEffect(() => {
    fetchSubTabCounts();
  }, [fetchSubTabCounts]);

  return {
    tab,
    setTab,
    subTab,
    setSubTab,
    rows,
    isLoading,
    tabCounts,
    subTabCounts,
    fetchRows,
    fetchTabCounts,
    fetchSubTabCounts,
  };
}
